import {keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {useToast} from "@/hooks/use-toast";
import * as apiClient from '@/lib/api'
import {ApiError} from "@/lib/api";
import {deleteArticle, unpublishArticleToAdvisers} from "@/modules/articles/article-editor/hooks";
import {AllowAdviserArticlesCreationSetting, ArticlesData} from "@/modules/articles/articles-table/types";
import {FetchParams} from "@/types/search";
import {FIVE_MINUTES} from "@/utils/constants";
import {QUERY_KEY_ALLOW_ADVISER_ARTICLES_CREATION, QUERY_KEY_ARTICLE, QUERY_KEY_ARTICLES} from '@/utils/keys'
import {ARTICLES_API_URL} from "@/utils/urls";

const { VITE_QUERY_STALE_TIME } = import.meta.env

export const buildQueryParamsString = (fetchParams: FetchParams): string => {
  const queryParams = new URLSearchParams()

  queryParams.append("limit", fetchParams.limit.toString())
  queryParams.append("offset", fetchParams.offset.toString())

  const orderByCol = fetchParams.order_by?.column ?? 'created_at'
  const orderByDirection = fetchParams.order_by?.direction ?? 'DESC'
  queryParams.append(`order_by[${orderByCol}]`, orderByDirection)

  return queryParams.toString()
}

function fetchArticles(fetchParams: FetchParams): Promise<ArticlesData> {
  return apiClient.get(`${ARTICLES_API_URL}?${buildQueryParamsString(fetchParams)}`)
}

export const articlesQueryOptions = (
  fetchParams: FetchParams,
) =>
  queryOptions({
    queryKey: [QUERY_KEY_ARTICLES, fetchParams],
    queryFn: () => fetchArticles(fetchParams),
    placeholderData: keepPreviousData,
    staleTime: VITE_QUERY_STALE_TIME ? Number(VITE_QUERY_STALE_TIME) : FIVE_MINUTES,
  });

export const useArticles = (fetchParams: FetchParams) => {
  return useQuery(articlesQueryOptions(fetchParams))
}

function fetchAllowAdviserArticlesCreation(): Promise<AllowAdviserArticlesCreationSetting> {
  return apiClient.get('/firm/articles/allow-adviser-article-creation')
}

export const allowAdviserArticlesCreationQueryOptions = () =>
  queryOptions({
    queryKey: [QUERY_KEY_ALLOW_ADVISER_ARTICLES_CREATION],
    queryFn: fetchAllowAdviserArticlesCreation,
    placeholderData: keepPreviousData,
  });

export const useAllowAdviserArticlesCreation = () => {
  return useQuery(allowAdviserArticlesCreationQueryOptions())
}

function updateAllowAdviserArticlesCreation(allowAdviserArticlesCreation: boolean) {
  return apiClient.put('/firm/articles/allow-adviser-article-creation', {allowAdviserArticlesCreation})
}

export const useUpdateAllowAdviserArticlesCreationMutation = () => {
  const queryClient = useQueryClient();
  const {toast} = useToast();

  return useMutation({
    mutationFn: updateAllowAdviserArticlesCreation,

    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [QUERY_KEY_ALLOW_ADVISER_ARTICLES_CREATION]});
      // Disabling the setting drafts advisers' published/in-review articles server-side, so the
      // list must be refetched to reflect their new status.
      queryClient.invalidateQueries({queryKey: [QUERY_KEY_ARTICLES]});
      toast({description: "Setting updated successfully", variant: "mint"});
    },

    onError: (error: unknown) => {
      queryClient.invalidateQueries({queryKey: [QUERY_KEY_ALLOW_ADVISER_ARTICLES_CREATION]});
      if (error instanceof ApiError && error.message) {
        toast({description: error.message, variant: "red"});
        return;
      }

      toast({description: "Something went wrong, please try again"});
    },
  });
}

export const useDeleteArticleMutation = (articleId: number) => {
  const queryClient = useQueryClient()
  const {toast} = useToast()

  return useMutation({
    mutationFn: () => deleteArticle(articleId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [QUERY_KEY_ARTICLES]})
      toast({description: 'Article deleted.', variant: 'mint'})
    },
    onError: () => {
      toast({description: 'Error deleting article.', variant: 'red'})
    },
  })
}

export const useUnpublishToAdvisersMutation = (articleId: number) => {
  const queryClient = useQueryClient()
  const {toast} = useToast()

  return useMutation({
    mutationFn: () => unpublishArticleToAdvisers(articleId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [QUERY_KEY_ARTICLES]})
      queryClient.invalidateQueries({queryKey: [QUERY_KEY_ARTICLE, articleId]})
      toast({description: 'Article unpublished from advisers.', variant: 'mint'})
    },
    onError: (e) => {
      toast({description: e instanceof ApiError && e.message ? e.message : 'Error unpublishing article from advisers.', variant: 'red'})
    },
  })
}
